/**
 * Giving momentum — a live ticker of recent gifts and a thermometer per
 * campaign. Donors who asked to stay anonymous are shown as a friend of the
 * ministry, never by name.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flame, TrendingUp, Users } from 'lucide-react';
import api from '@/lib/api';
import { campaignProgress, money } from './campaignProgress';

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

export const GivingTicker = () => {
  const { data } = useQuery({
    queryKey: ['giving-momentum'],
    queryFn: () => api.get('/campaigns/momentum/recent?limit=12').then((r) => r.data),
    refetchInterval: 20000,
  });

  const gifts = data?.gifts || [];
  if (gifts.length === 0) return null;

  return (
    <section className="border-y border-[color:var(--fwm-line)] bg-[color:var(--fwm-ground-2)]"
             data-testid="giving-ticker">
      <div className="mx-auto max-w-[1180px] px-6 lg:px-10 py-5 flex flex-wrap items-center gap-x-8 gap-y-3">
        <p className="fwm-kicker flex items-center gap-2 shrink-0">
          <Flame className="h-3.5 w-3.5" /> Recently given
        </p>

        <div className="flex-1 min-w-0 sm:min-w-[260px] overflow-hidden">
          <div className="flex gap-8 animate-[fwm-marquee_38s_linear_infinite] hover:[animation-play-state:paused]">
            {[...gifts, ...gifts].map((g, i) => (
              <span key={`${g.id}-${i}`} className="whitespace-nowrap text-sm text-[color:var(--fwm-body)]"
                    data-testid={i < gifts.length ? `ticker-gift-${g.id}` : undefined}>
                <strong className="text-[color:var(--fwm-ink)]">{g.giver}</strong>
                {g.town ? ` from ${g.town}` : ''} gave{' '}
                <strong className="text-[color:var(--fwm-accent-dark)]">
                  {money(g.amount, g.currency)}
                </strong>
                {g.campaign ? ` to ${g.campaign}` : ''}
                <span className="text-[color:var(--fwm-muted)]"> · {ago(g.at)}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-6 text-xs text-[color:var(--fwm-muted)] shrink-0">
          <span className="flex items-center gap-1.5" data-testid="ticker-today">
            <TrendingUp className="h-3.5 w-3.5" />
            {money(data.today_total, data.currency)} today
          </span>
          <span className="flex items-center gap-1.5" data-testid="ticker-givers">
            <Users className="h-3.5 w-3.5" /> {data.givers} givers
          </span>
        </div>
      </div>
      <style>{`
        @keyframes fwm-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export const CampaignThermometer = ({ campaign }) => {
  const { data } = useQuery({
    queryKey: ['campaign-momentum', campaign.slug],
    queryFn: () => api.get(`/campaigns/${campaign.slug}/momentum`).then((r) => r.data),
    enabled: !!campaign.slug,
    refetchInterval: 60000,
  });
  const p = campaignProgress(data || campaign);
  const marks = [25, 50, 75];

  return (
    <div className="mt-7" data-testid={`thermometer-${campaign.slug}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 text-sm">
        <span className="fwm-display text-2xl" data-testid={`giving-raised-${campaign.slug}`}>
          {money(p.raised, p.currency)}
        </span>
        {p.needsTarget ? (
          <span className="fwm-badge" data-testid={`giving-no-target-${campaign.slug}`}>
            Goal to be announced
          </span>
        ) : (
          <span className="text-[color:var(--fwm-muted)] tabular-nums"
                data-testid={`giving-target-${campaign.slug}`}>
            {p.label} of {money(p.target, p.currency)}
          </span>
        )}
      </div>

      {/* Thermometer — a filling column with quarter marks */}
      <div className="mt-4 relative h-4 w-full bg-[color:var(--fwm-line)] rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-[color:var(--fwm-accent)] transition-all duration-1000"
             style={{ width: `${Math.max(p.pct, p.raised > 0 ? 1.5 : 0)}%` }} />
        {!p.needsTarget && marks.map((m) => (
          <span key={m} className="absolute top-0 h-full w-px bg-[color:var(--fwm-ground)]/70"
                style={{ left: `${m}%` }} />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[color:var(--fwm-muted)]">
        <span data-testid={`thermo-givers-${campaign.slug}`}>{data?.givers ?? 0} givers</span>
        {(data?.last_7_days ?? 0) > 0 && (
          <span data-testid={`thermo-week-${campaign.slug}`}>
            {money(data.last_7_days, p.currency)} in the last 7 days
          </span>
        )}
        {(data?.gifts || []).slice(0, 1).map((g) => (
          <span key={g.id} data-testid={`thermo-latest-${campaign.slug}`}>
            Latest: {g.giver} · {money(g.amount, g.currency)} · {ago(g.at)}
          </span>
        ))}
      </div>
    </div>
  );
};

export default GivingTicker;
